<template>
  <b-modal
    v-model="modalShow"
    size="sm"
    centered
    title="Print Rack Group Report"
    @hidden="$emit('modal-closed')"
  >
    <div>
      <b-form @submit.prevent="submitHandler">
        <b-form-group
          label="User"
        >
          <custom-v-select
            v-model="selectedUserId"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="operationUsers"
            label="name"
            placeholder="Select User"
            :reduce="(item) => item.id"
          />
        </b-form-group>
      </b-form>
    </div>

    <template #modal-footer="{ cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="!selectedUserId || isDownloading"
        @click="submitHandler"
      >
        Print
        <b-spinner
          v-if="isDownloading"
          small
          label="Small Spinner"
        />
      </b-button>

    </template>
  </b-modal>
</template>

<script>
import {
  BButton,
  BSpinner,
  BForm,
  BFormGroup,
} from 'bootstrap-vue'
import axios from 'axios'
import * as XLSX from 'xlsx'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BButton,
    BSpinner,
    BFormGroup,
    BForm,
    CustomVSelect,
  },
  props: {
    operationUsers: {
      type: Array,
      required: true,
    },
    rgIds: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      modalShow: true,
      submitting: false,
      selectedUserId: null,
      isDownloading: false,
    }
  },
  methods: {
    async submitHandler() {
      try {
        const payload = { ids: this.rgIds, operation_user_id: this.selectedUserId }

        this.isDownloading = true
        const response = await axios.post('/operations/download_rack_group_report', payload, {
          responseType: 'blob',
        })

        await this.printXlsx(response.data)
        // This will run whether user prints or cancels
        this.modalShow = false
        this.$emit('downloaded')
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.detail || 'Failed to download!',
            icon: 'CheckIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isDownloading = false
      }
    },
    printXlsx(xlsxBlob) {
      return new Promise((resolve, reject) => {
        xlsxBlob.arrayBuffer().then(arrayBuffer => {
          try {
            const workbook = XLSX.read(arrayBuffer, { type: 'array' })

            let allTablesHTML = ''

            workbook.SheetNames.forEach((sheetName, index) => {
              const worksheet = workbook.Sheets[sheetName]
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

              if (workbook.SheetNames.length > 1) {
                allTablesHTML += `<h3 style="font-size: 14px; margin: 20px 0 10px 0; background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; color-adjust: exact; padding: 8px; border: 1px solid #ccc; page-break-inside: avoid;">${sheetName}</h3>`
              }

              allTablesHTML += '<table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; page-break-inside: avoid;">'

              jsonData.forEach((row, rowIndex) => {
                // Check if this row contains a title/header by looking for "QTY" in the next row
                let isGroupHeader = false
                if (rowIndex < jsonData.length - 1) {
                  const nextRow = jsonData[rowIndex + 1]
                  isGroupHeader = row.length === 1 && row[0]
                && nextRow && nextRow.some(cell => cell && cell.toString().toUpperCase().includes('QTY'))
                }

                if (isGroupHeader) {
                  // Handle group header rows
                  allTablesHTML += `<tr><td colspan="20" style="background-color: #e6f3ff !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-weight: bold; font-size: 14px; border: 2px solid #000; padding: 5px 10px; text-align: left; page-break-inside: avoid;">${row[0]}</td></tr>`
                } else {
                  allTablesHTML += '<tr>'

                  const maxCols = Math.max(...jsonData.filter((dataRow, idx) => {
                    // Skip title rows (those followed by QTY rows)
                    if (idx < jsonData.length - 1) {
                      const nextRow = jsonData[idx + 1]
                      if (dataRow.length === 1 && dataRow[0]
                      && nextRow && nextRow.some(cell => cell && cell.toString().toUpperCase().includes('QTY'))) {
                        return false
                      }
                    }
                    return true
                  }).map(dataRow => dataRow.length))

                  for (let colIndex = 0; colIndex < maxCols; colIndex += 1) {
                    const cellValue = row[colIndex] || ''

                    if (rowIndex === 0 || (rowIndex > 0 && jsonData[rowIndex - 1] && jsonData[rowIndex - 1].length === 1)) {
                      // Header row or row following a group header
                      allTablesHTML += `<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 14px; border: 1px solid #000; padding: 4px 6px; text-align: left; white-space: nowrap; page-break-inside: avoid;">${cellValue}</th>`
                    } else {
                      // Regular data row
                      const bgColor = rowIndex % 2 === 0 ? '#ffffff' : '#f8f8f8'
                      allTablesHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 12px; border: 1px solid #000; padding:2px 4px; text-align: left; white-space: nowrap;">${cellValue}</td>`
                    }
                  }
                  allTablesHTML += '</tr>'
                }
              })

              allTablesHTML += '</table>'

              if (index < workbook.SheetNames.length - 1) {
                allTablesHTML += '<div style="height: 30px; page-break-inside: avoid;"></div>'
              }
            })

            const iframe = document.createElement('iframe')
            iframe.style.position = 'absolute'
            iframe.style.top = '-9999px'
            iframe.style.left = '-9999px'
            document.body.appendChild(iframe)

            // Enhanced HTML with better print styles
            iframe.contentDocument.open()
            iframe.contentDocument.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              @media print {
                * {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                
                body {
                  margin: 15px;
                  font-family: Arial, sans-serif;
                }
                
                table {
                  page-break-inside: auto;
                }
                
                tr {
                  page-break-inside: avoid;
                  page-break-after: auto;
                }
                
                th, td {
                  page-break-inside: avoid;
                }
                
                h3 {
                  page-break-after: avoid;
                }
              }
              
              @page {
                margin: 0.5in;
                size: A4;
              }
              
              body {
                font-family: Arial, sans-serif;
                margin: 15px 5px;
              }
            </style>
          </head>
          <body>
            ${allTablesHTML}
          </body>
          </html>
        `)
            iframe.contentDocument.close()

            setTimeout(() => {
              iframe.contentWindow.print()

              iframe.contentWindow.addEventListener('afterprint', () => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe)
                }
                resolve('Print completed successfully')
              })

              // Fallback cleanup
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe)
                  resolve('Print completed')
                }
              }, 500)
            }, 500)
          } catch (error) {
            reject(error)
          }
        }).catch(error => {
          reject(error)
        })
      })
    },
  },
}
</script>
